import React, { useEffect, useRef, useState } from 'react';
import styles from './CoverageCheckView.module.scss';
import { ServiceOptions, LoadingSpinner } from 'app/components';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as AttachIcon } from 'app/assets/Icons/AttachWhite.svg';
import { getCoverageCheck } from 'integration';
import { COVERAGE_CHECK_STEPS, SERVICE_NAMES } from 'app/utils/consts';
import {
  getBusinessArea,
  getService,
  setCurrentFile,
  useAppDispatch,
  useAppSelector
} from 'app/redux';
import { ActionButtons } from 'app/components/ActionButtons/ActionButtons';
import { UploadedFileCard } from 'app/components/Sections/UploadedDocument/UploadedDocument';
import { useNavigate } from 'react-router-dom';
import config from 'config';
import { handleFileValidation } from 'app/utils';

export type FileType = 'FNOL' | 'POLICY' | 'INVOICE';

export const CoverageCheckView: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [coverageResponse, setCoverageResponse] = useState<string>();

  const [hasInvoice, setHasInvoice] = useState(false);

  const [FNOLText, setFNOLText] = useState<string>('');
  const [FNOLFile, setFNOLFile] = useState<File | null>(null);
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [FNOLFileError, setFNOLFileError] = useState<string | undefined>();
  const [policyFileError, setPolicyFileError] = useState<string | undefined>();
  const [invoiceFileError, setInvoiceFileError] = useState<
    string | undefined
  >();

  const checkCoverage = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const FNOLResponse = (
        await getCoverageCheck(
          String(currentBusinessArea),
          String(currentService),
          createRequestBody(FNOLFile as any, COVERAGE_CHECK_STEPS.FNOL),
          COVERAGE_CHECK_STEPS.FNOL
        )
      ).analysisResult;
      const policyResponse: { raw: string } = (
        await getCoverageCheck(
          String(currentBusinessArea),
          String(currentService),
          createRequestBody(
            policyFile as any,
            COVERAGE_CHECK_STEPS.POLICY,
            FNOLResponse.raw
          ),
          COVERAGE_CHECK_STEPS.POLICY
        )
      ).analysisResult;

      let response: string = policyResponse.raw;

      if (invoiceFile) {
        const invoiceResponse: { raw: string } = (
          await getCoverageCheck(
            String(currentBusinessArea),
            String(currentService),
            createRequestBody(
              invoiceFile as any,
              COVERAGE_CHECK_STEPS.INVOICE,
              policyResponse.raw
            ),
            COVERAGE_CHECK_STEPS.INVOICE
          )
        ).analysisResult;
        response += '\n\nInvoice:\n\n' + invoiceResponse.raw;
      }

      setCoverageResponse(response);
      setIsLoading(false);
    } catch (error: unknown) {
      let extractedMessage: string | undefined;
      if (error && typeof error === 'object') {
        const anyErr = error as any;
        extractedMessage = anyErr?.response?.data?.message || anyErr?.message;
      }
      console.error('CoverageCheck processing failed:', error);
      if (extractedMessage) {
        console.error('Error message:', extractedMessage);
        setFNOLFileError(extractedMessage);
        setPolicyFileError(extractedMessage);
        setInvoiceFileError(extractedMessage);
      }
      deleteFiles();
      navigate(config.APP.PATH.LANDING);
    }
  };

  const createRequestBody = (
    file: File,
    step: string,
    context?: string
  ): FormData => {
    const body = new FormData();
    body.append('file', file, file.name);
    if (
      context &&
      (step === COVERAGE_CHECK_STEPS.POLICY ||
        step === COVERAGE_CHECK_STEPS.INVOICE)
    ) {
      body.append('context', context);
    }

    return body;
  };

  const fileTypeMap = {
    FNOL: {
      extentions: '.json,.txt,.mp4,.mp3,.wav,.m4a,.mpeg',
      setFile: setFNOLFile,
      setError: setFNOLFileError,
      service: SERVICE_NAMES.FNOL_CAPTURING
    },
    POLICY: {
      extentions: '.pdf',
      setFile: setPolicyFile,
      setError: setPolicyFileError,
      service: ''
    },
    INVOICE: {
      extentions: '.png,.jpg,.jpeg,.bmp,.tiff,.heif,.pdf',
      setFile: setInvoiceFile,
      setError: setInvoiceFileError,
      service: SERVICE_NAMES.INVOICE_EXTRACTOR
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: FileType
  ): void => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = handleFileValidation(file, fileTypeMap[type].service);
      if (validation.valid) {
        const setFile = fileTypeMap[type].setFile;
        setFile(file);
        return;
      }
      const errorMessage = validation.error;
      console.error(errorMessage);
      const setError = fileTypeMap[type].setError;
      setError(errorMessage);
    }
  };

  const handleFileSelection = (type: FileType): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = fileTypeMap[type].extentions;
    input.multiple = false;
    input.onchange = (e: any) => handleFileChange(e, type);
    input.click();
  };

  const handleCheckboxChange = (): void => {
    setHasInvoice(!hasInvoice);
  };

  const handleFNOLText = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setFNOLText(e.target.value);
  };

  const textToFile = (text: string, fileName: string): File => {
    const config = { type: 'text/plain' };
    const blob = new Blob([text], config);

    return new File([blob], fileName, config);
  };

  const deleteFiles = (): void => {
    setFNOLFile(null);
    setPolicyFile(null);
    setInvoiceFile(null);
    setCoverageResponse(undefined);
    setFNOLText('');
    setFNOLFileError(undefined);
    setPolicyFileError(undefined);
    setInvoiceFileError(undefined);
  };

  useEffect(() => {
    const createFNOLFile = setTimeout(() => {
      if (FNOLText.trim() !== '') {
        const file = textToFile(FNOLText, 'loss_description.txt');
        dispatch(setCurrentFile(file));
        setFNOLFile(file);
      }
    }, 1000);

    return () => clearTimeout(createFNOLFile);
  }, [FNOLText]);

  return (
    <div className={styles['coverage-check']}>
      <div className={styles['service-options']}>
        <ServiceOptions />
      </div>
      <ActionButtons
        handleDelete={deleteFiles}
        handleInvoiceSwitch={handleCheckboxChange}
        isSwitchOn={hasInvoice}
        showDeleteFile={true}
        isPlural={true}
      />
      <div className={styles.wrapper}>
        <div className={styles['input-section']}>
          {FNOLFile == null || policyFile == null ? (
            <div className={styles.upload}>
              <h3>Describe your loss &nbsp;&nbsp;&nbsp;or</h3>
              <div>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => handleFileSelection('FNOL')}
                >
                  <Button.Label>Upload FNOL</Button.Label>
                  <Button.Icon>
                    <AttachIcon />
                  </Button.Icon>
                </Button>
                <span className={styles.error}>{FNOLFileError}</span>
              </div>
              {''}
            </div>
          ) : (
            <UploadedFileCard file={FNOLFile} />
          )}

          {(FNOLFile == null || policyFile == null) && (
            <div className={styles['text-area']}>
              <textarea
                onChange={handleFNOLText}
                placeholder="Please enter a brief description of your loss"
              />
            </div>
          )}

          {policyFile == null ? (
            <div className={styles.upload}>
              <div className={styles.margin}>
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => handleFileSelection('POLICY')}
                  disabled={FNOLFile == null}
                >
                  <Button.Label>Upload Policy to check</Button.Label>
                  <Button.Icon>
                    <AttachIcon />
                  </Button.Icon>
                </Button>
                <span className={styles.error}>{policyFileError}</span>
              </div>
            </div>
          ) : (
            <UploadedFileCard file={policyFile} />
          )}

          {hasInvoice &&
            (invoiceFile == null ? (
              <div className={styles.upload}>
                <div className={styles.margin}>
                  <Button
                    variant="tertiary"
                    size="small"
                    onClick={() => handleFileSelection('INVOICE')}
                    disabled={FNOLFile == null || policyFile == null}
                  >
                    <Button.Label>Upload Invoice</Button.Label>
                    <Button.Icon>
                      <AttachIcon />
                    </Button.Icon>
                  </Button>
                  <span className={styles.error}>{invoiceFileError}</span>
                </div>
              </div>
            ) : (
              <UploadedFileCard file={invoiceFile} />
            ))}

          {coverageResponse == null && (
            <div className={styles['check-btn']}>
              <Button
                onClick={checkCoverage}
                disabled={FNOLFile == null || policyFile == null}
                variant="secondary"
              >
                <Button.Label>Check Coverage</Button.Label>
              </Button>
            </div>
          )}
        </div>
        {coverageResponse != null && (
          <div style={{ marginTop: '-150px' }}>
            <div style={{ justifyContent: 'left' }}>
              <h3 className={styles.title}>{'Coverage check results'}</h3>
            </div>
            <div className={styles['output-section']}>
              <textarea
                ref={textareaRef}
                id={'textarea-coverage-check'}
                disabled={true}
                className={styles.textarea}
                value={coverageResponse}
              />
            </div>
          </div>
        )}
      </div>

      {isLoading && <LoadingSpinner />}
    </div>
  );
};
